/**
 * Quiz Setup Screen
 * Configure quiz settings before starting (free play mode)
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";

/**
 * Question count options
 */
const QUESTION_COUNT_OPTIONS = [5, 10, 15, 20];

/**
 * Difficulty options
 */
const DIFFICULTY_OPTIONS = [
  { value: null, label: "Alle", icon: "shuffle", color: "#6366F1" },
  { value: "easy", label: "Makkelijk", icon: "happy", color: "#10B981" },
  { value: "medium", label: "Gemiddeld", icon: "cafe", color: "#F59E0B" },
  { value: "hard", label: "Moeilijk", icon: "flame", color: "#EF4444" },
];

interface QuizCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
}

export default function QuizSetupScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ categoryId?: string; mode?: string }>();
  const colors = useThemeColors();

  const categoryId = params.categoryId === "all" ? null : params.categoryId;

  // State
  const [category, setCategory] = useState<QuizCategory | null>(null);
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  /**
   * Fetch category info
   */
  useEffect(() => {
    const fetchCategory = async () => {
      if (!categoryId) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("quiz_categories")
        .select("id, name, icon, color")
        .eq("id", categoryId)
        .single();

      if (!error && data) {
        setCategory(data);
      }
      setIsLoading(false);
    };

    fetchCategory();
  }, [categoryId]);

  /**
   * Handle start quiz
   */
  const handleStartQuiz = () => {
    setIsStarting(true);
    
    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.set("mode", "free");
    if (categoryId) queryParams.set("categoryId", categoryId);
    if (difficulty) queryParams.set("difficulty", difficulty);
    queryParams.set("count", questionCount.toString());

    router.push(`/(tabs)/games/quiz/play?${queryParams.toString()}`);
  };

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <ActivityIndicator color={colors.primary} size="large" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text className="text-2xl font-bold" style={{ color: colors.text }}>
              Quiz Instellen
            </Text>
            <Text style={{ color: colors.textSecondary }}>
              Vrij Spelen
            </Text>
          </View>
        </View>

        {/* Selected Category */}
        <View className="px-4 mt-2">
          <View
            className="rounded-xl p-4"
            style={{
              backgroundColor: category ? `${category.color}15` : "#6366F115",
              borderWidth: 2,
              borderColor: category ? category.color : "#6366F1",
            }}
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-4"
                style={{ backgroundColor: category ? `${category.color}25` : "#6366F125" }}
              >
                <Ionicons
                  name={(category?.icon || "apps") as any}
                  size={24}
                  color={category?.color || "#6366F1"}
                />
              </View>
              <View>
                <Text className="text-lg font-bold" style={{ color: colors.text }}>
                  {category?.name || "Alle Categorieën"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  {category ? "Geselecteerde categorie" : "Mix van alle vragen"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Question Count */}
        <View className="px-4 mt-6">
          <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
            Aantal Vragen
          </Text>

          <View className="flex-row">
            {QUESTION_COUNT_OPTIONS.map((count, index) => (
              <TouchableOpacity
                key={count}
                onPress={() => setQuestionCount(count)}
                className="flex-1 rounded-xl py-4 items-center"
                style={{
                  backgroundColor: questionCount === count
                    ? `${colors.primary}20`
                    : colors.surface,
                  borderWidth: 2,
                  borderColor: questionCount === count
                    ? colors.primary
                    : colors.border,
                  marginRight: index < 3 ? 8 : 0,
                }}
                activeOpacity={0.7}
              >
                <Text
                  className="text-xl font-bold"
                  style={{
                    color: questionCount === count
                      ? colors.primary
                      : colors.text,
                  }}
                >
                  {count}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Difficulty Selection */}
        <View className="px-4 mt-6">
          <Text className="text-lg font-semibold mb-3" style={{ color: colors.text }}>
            Moeilijkheidsgraad
          </Text>

          <View className="flex-row flex-wrap">
            {DIFFICULTY_OPTIONS.map((option, index) => (
              <TouchableOpacity
                key={option.value || "all"}
                onPress={() => setDifficulty(option.value)}
                className="w-[23%] rounded-xl p-3 items-center"
                style={{
                  backgroundColor: difficulty === option.value
                    ? `${option.color}20`
                    : colors.surface,
                  borderWidth: 2,
                  borderColor: difficulty === option.value
                    ? option.color
                    : colors.border,
                  marginRight: index < 3 ? "2.66%" : 0,
                }}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={option.icon as any}
                  size={24}
                  color={option.color}
                />
                <Text
                  className="text-xs font-medium mt-1 text-center"
                  style={{
                    color: difficulty === option.value
                      ? option.color
                      : colors.textSecondary,
                  }}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Info */}
        <View
          className="mx-4 mt-6 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
        >
          <View className="flex-row items-center mb-2">
            <Ionicons name="information-circle" size={18} color={colors.primary} />
            <Text className="ml-2 font-semibold" style={{ color: colors.text }}>
              Vrij Spelen
            </Text>
          </View>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            In vrij spelen modus verdien je geen punten voor je team, 
            maar kun je wel je kennis oefenen!
          </Text>
        </View>

        <View className="h-32" />
      </ScrollView>

      {/* Start Button */}
      <View
        className="absolute bottom-0 left-0 right-0 p-4"
        style={{ backgroundColor: colors.background }}
      >
        <TouchableOpacity
          onPress={handleStartQuiz}
          disabled={isStarting}
          className="rounded-2xl py-4 flex-row items-center justify-center"
          style={{
            backgroundColor: isStarting ? colors.border : colors.primary,
          }}
          accessibilityRole="button"
          accessibilityLabel="Start Quiz"
        >
          {isStarting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <>
              <Ionicons name="play" size={22} color="#FFFFFF" />
              <Text className="ml-2 text-lg font-bold text-white">
                Start Quiz
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

